/* "From your library" — the one picker every tool uses.
 *
 * Her order, 2026-09-17: quotes go in WORD FOR WORD (a testimonial into a
 * Testimonials section or a Quote slide); stories and objections are context
 * for Brill and never pasted, which is the drafting chat's job, not this
 * button's.
 *
 * Knows nothing about a page, a slide or a script. It is handed the kinds it
 * may offer, what to do with the pick, and `usedAs` — {type, id, label} —
 * which is the stamp it writes back into the Library so the item can say
 * "Used in: Home page". Picking the same item into the same record twice does
 * not count twice.
 *
 * A testimonial with no permission is SHOWN and not pickable, rather than
 * hidden: she needs to see that it is the permission stopping her, or she goes
 * looking for a quote she knows she wrote.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Lightbulb, BookOpen, Quote, MessageCircleQuestion } from "lucide-react";
import { B, btnGhost, input } from "./tokens";
import { floatingPanel, useAnchoredPanel } from "./ui";
import {
  LIBRARY_KINDS, useLibrary, libraryView, metaFor, usedLabel, noteUse, usedByStamp, needsPermission,
} from "../../utils/library";

/* EVERY BUCKET, THEN PICK (her call, 2026-09-28: "all the things that load
   ideas need access to all the buckets not just ideas, definitely stories —
   it should probably just always be everything and then it can be selected").
   A picker that hands Brill CONTEXT offers all four kinds with a filter row.
   A picker that pastes WORD FOR WORD (a testimonial into a quote) keeps its one
   kind — an idea pasted as a client's quote is the mistake this prevents. */
export const ALL_LIBRARY_KINDS = LIBRARY_KINDS.map((k) => k.id);

const ICON = { idea: Lightbulb, story: BookOpen, testimonial: Quote, objection: MessageCircleQuestion };

/**
 * @param kinds      which kinds to offer, e.g. ["testimonial"]
 * @param requirePermission true where the pick ends up in front of clients
 * @param usedAs     {type, id, label} — what the pick is being used by
 * @param onPick     (item) => void. The caller writes the words wherever they go
 * @param label      the button's words
 */
export function LibraryPickButton({ kinds, requirePermission = false, usedAs, onPick, label = "From your library" }) {
  const { ref: btn, anchor, open, toggle, close } = useAnchoredPanel();

  /* IT FLOATS, IT DOES NOT DROP. On the drafting screen this button sits at the
     very bottom of the chat card, and that card is `overflow: hidden` — so a
     popover positioned inside it was CLIPPED AWAY ENTIRELY. She pressed the
     button and nothing appeared (her report, 2026-09-17: "the badge isn't
     showing the dropdown; it will need to pop up above the fold so that you
     don't have to scroll down since the button is at the bottom").

     So the list is `position: fixed`, measured off the button when she opens it
     — a chat pane scrolls and grows, so the measurement cannot be taken any
     earlier — and it opens UPWARDS whenever there is more room above. That is
     `useAnchoredPanel` / `floatingPanel` in ui.jsx now: the offer's "Where it
     sends people" picker had the same bug five days later, and one of them had
     to be the shared one.

     THE LIBRARY IS ALSO READ ONLY WHEN SHE OPENS IT. The rows live in the list
     below, so a screen that merely OFFERS a pick — every drafting screen does
     now — costs no query until she asks for one. */
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button ref={btn} type="button" aria-expanded={open} onClick={toggle}
        style={{ ...btnGhost, padding: "7px 13px", fontSize: 12, borderColor: B.tealLine, color: B.teal }}>
        {label}
      </button>
      {open && (
        <PickList kinds={kinds} requirePermission={requirePermission} usedAs={usedAs} anchor={anchor}
          onPick={onPick} onClose={close} />
      )}
    </div>
  );
}

function PickList({ kinds, requirePermission, usedAs, onPick, onClose, anchor }) {
  const { rows, loading, error, save } = useLibrary();
  const [q, setQ] = useState("");
  /* "all" until she narrows it — the chips only when there is more than one
     kind to choose between. */
  const [only, setOnly] = useState("all");

  const options = useMemo(() => {
    const kind = kinds.length === 1 ? kinds[0] : only;
    return libraryView(rows, { kind, sort: "unused", q }).filter((it) => kinds.includes(it.kind));
  }, [rows, kinds, q, only]);
  const count = (k) => rows.filter((it) => (k === "all" ? kinds.includes(it.kind) : it.kind === k)).length;

  /* Escape closes it, like every other overlay here. */
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const take = async (item) => {
    onClose();
    onPick(item);
    if (usedAs?.id) await save({ ...item, usedBy: noteUse(item, usedByStamp(usedAs.type, usedAs.id, usedAs.label)) });
  };

  /* Above or below, whichever has room, never off an edge, and never taller
     than the side it opens on — all of that is `floatingPanel` now. It used to
     flip on a fixed 340px threshold and then ask for `min(420px, 70vh)`
     regardless, which on a short window was still taller than the gap it had. */
  return (
    <div role="dialog" aria-label="Pick from your library"
          style={{
            ...floatingPanel(anchor, { width: 420, wants: 420 }),
            background: B.surface, border: `1px solid ${B.border2}`, borderRadius: 12, boxShadow: "var(--shadow-menu)",
            padding: 12, display: "flex", flexDirection: "column", gap: 8,
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label htmlFor="libpick-q" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Search your library</label>
            <input id="libpick-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search"
              style={{ ...input, padding: "7px 11px", fontSize: 12.5 }} />
            <button type="button" onClick={onClose} style={{ ...btnGhost, padding: "6px 10px", fontSize: 12 }}>Close</button>
          </div>

          {kinds.length > 1 && (
            <div role="radiogroup" aria-label="Which kind" style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {["all", ...kinds].map((k) => {
                const on = only === k;
                const label = k === "all" ? "All" : (LIBRARY_KINDS.find((x) => x.id === k)?.plural || k);
                return (
                  <button key={k} type="button" role="radio" aria-checked={on} onClick={() => setOnly(k)}
                    style={{ padding: "4px 10px", borderRadius: 14, fontSize: 11.5, fontFamily: "inherit", cursor: "pointer",
                      border: `1px solid ${on ? B.teal : B.border2}`, background: on ? B.tealQuiet : "transparent",
                      color: on ? B.teal : B.mid, fontWeight: on ? 700 : 500 }}>
                    {label} <span style={{ color: B.faint, fontWeight: 400 }}>{count(k)}</span>
                  </button>
                );
              })}
            </div>
          )}

          {error && <div style={{ fontSize: 12, color: B.salmon }}>{error}</div>}

          <div style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 280, overflowY: "auto" }}>
            {loading && options.length === 0 && <div style={{ fontSize: 12.5, color: B.dim, padding: "10px 4px" }}>Loading…</div>}
            {!loading && options.length === 0 && (
              <div style={{ fontSize: 12.5, color: B.dim, padding: "10px 4px", lineHeight: 1.6 }}>
                Nothing here yet. Jot these down in Run → Library and they'll show up.
              </div>
            )}
            {options.map((item) => {
              const Icon = ICON[item.kind] || Quote;
              const blocked = requirePermission && needsPermission(item);
              return (
                <button key={item.id} type="button" disabled={blocked} onClick={() => take(item)}
                  title={blocked ? "Tick permission in the Library before using this publicly" : undefined}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 10, width: "100%", textAlign: "left",
                    padding: "9px 10px", borderRadius: 9, border: "none", background: "transparent",
                    cursor: blocked ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: blocked ? 0.55 : 1,
                  }}>
                  <Icon size={14} style={{ flexShrink: 0, marginTop: 2, color: B.dim }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13, color: B.text, lineHeight: 1.45 }}>{item.text}</span>
                    <span style={{ display: "block", fontSize: 11, color: B.dim, marginTop: 2 }}>
                      {metaFor(item)} · {usedLabel(item)}{blocked ? " · no permission yet" : ""}
                    </span>
                  </span>
                </button>
              );
            })}
      </div>
    </div>
  );
}
